import React, { useState } from 'react'
import {
  Box,
  Button,
  Heading,
  Typography,
  Frame,
  FormControlLabel
} from 'components'
import { FormGroup, Chip, Radio as MuiRadio, RadioGroup, Checkbox, FormControlLabel as MUIFormControlLabel } from '@material-ui/core'
import { makeStyles, withStyles } from '@material-ui/core/styles'
import { ProgressBarFooter } from './FreelancerProgressBarFooter'
import firebase from 'utils/firebase'
import { useContent, useHistory } from 'utils'

const useStyles = makeStyles((theme) => ({
  previewTag: {
    backgroundColor: '#ffffff',
    border: '1px solid #e0e0e0',
    borderRadius: '16px',
    color: theme.palette.text.primary,
    fontWeight: 500,
  },
  tagHeader: {
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(3),
  },
  checkboxContainer: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: theme.spacing(2),
  },
  checkboxWrapper: {
    display: 'flex',
    alignItems: 'flex-start',
    marginBottom: theme.spacing(1),
  },
  checkbox: {
    padding: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
  labelContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  radioButton: {
    '&.MuiRadio-root': {
      padding: 12,
    },
    '& .MuiSvgIcon-root': {
      display: 'none',
    },
    '& .MuiIconButton-label:after': {
      content: '""',
      display: 'block',
      width: 18,
      height: 18,
      backgroundColor: 'white',
      border: '2px solid #999',
    },
    '&.Mui-checked .MuiIconButton-label:after': {
      backgroundColor: theme.palette.primary.main,
      border: '2px solid ' + theme.palette.primary.main,
    },
  },
  formControlLabel: {
    marginBottom: theme.spacing(1),
    '& .MuiFormControlLabel-label': {
      fontSize: '1rem',
      marginLeft: theme.spacing(1),
    },
  },
}))

// VisibleCheckbox will be created in the component using colors from useContent

const FreelancerDisabilitySelect = ({
  onboarding,
  user,
  stepIndex,
  totalSteps,
  onNext = () => { },
  onSubmit = () => { }
}) => {
  const [disabilityStatus, setDisabilityStatus] = useState(user?.disabilityStatus || '')
  const [selectedDisabilities, setSelectedDisabilities] = useState(user?.selectedDisabilities || [])
  const { landing } = useContent() || {}
  const history = useHistory()
  const buttonColor = landing?.['onboarding-button-color'] || 'primary'
  const buttonSecondaryColor = landing?.['onboarding-button-secondary'] || '#6c757d'

  // Create VisibleCheckbox component with dynamic colors
  const VisibleCheckbox = withStyles({
    root: {
      '&$checked': {
        color: buttonColor,
      },
      color: buttonSecondaryColor,
    },
    checked: {},
  })((props) => <Checkbox {...props} />);

  // Create styled Radio component with dynamic colors
  const StyledRadio = withStyles({
    root: {
      '&$checked': {
        color: buttonColor,
      },
      color: buttonSecondaryColor,
    },
    checked: {},
  })(MuiRadio);

  const handleDisabilityChange = (event) => {
    const { value, checked } = event.target

    if (checked) {
      setSelectedDisabilities([...selectedDisabilities, value])
    } else {
      setSelectedDisabilities(selectedDisabilities.filter(item => item !== value))
    }
  }

  const handleSubmit = () => {
    if (disabilityStatus) {
      // Save to user profile
      if (onboarding) {
        firebase.analytics().logEvent('user_freelancer_disability_status_submit_click')
      }

      // Include all relevant data
      const data = {
        disabilityStatus,
        ...(disabilityStatus === 'Yes' ? { selectedDisabilities } : { selectedDisabilities: [] })
      }

      // console.log('Updating user with disability data:', data);

      // Use direct Firebase update with set and merge to ensure data is saved properly
      firebase
        .firestore()
        .collection('users')
        .doc(user.id)
        .set(data, { merge: true })
        .then(() => {
          // console.log('Disability information successfully updated in database')

          if (onSubmit) {
            onSubmit(data)
          }

          if (onboarding && onNext) {
            // If in onboarding mode, call onNext
            onNext()
          } else if (!onboarding) {
            // If not in onboarding mode, redirect back to profile
            history.push('/profile')
          }
        })
        .catch(error => {
          console.error('Error updating disability information:', error)
        })
    }
  }

  const classes = useStyles();

  return (
    <Frame maxWidth="xs" user={user}>
      <Box mx="auto" maxWidth={430}>
        <Box display="flex" alignItems="center" mb={2}>
          <Heading style={{ marginRight: '8px' }}>
            Do you identify as a person with a disability?
          </Heading>
          <Typography variant="subtitle1" color="textSecondary">
            (Optional)
          </Typography>
        </Box>

        <Box my={3}>
          <RadioGroup
            aria-label="disability-status"
            name="disability-status"
            value={disabilityStatus}
            onChange={(e) => setDisabilityStatus(e.target.value)}
          >
            <MUIFormControlLabel
              value="Yes"
              control={<StyledRadio className={classes.radioButton} />}
              label="Yes"
              className={classes.formControlLabel}
            />
            <MUIFormControlLabel
              value="No"
              control={<StyledRadio className={classes.radioButton} />}
              label="No"
              className={classes.formControlLabel}
            />
            <MUIFormControlLabel
              value="Prefer not to answer"
              control={<StyledRadio className={classes.radioButton} />}
              label="Prefer not to answer"
              className={classes.formControlLabel}
            />
          </RadioGroup>
        </Box>

        {disabilityStatus === 'Yes' && (
          <>
            <Box mt={3}>
              <Typography variant="h6" className={classes.tagHeader}>
                On your profile this tag will appear.
              </Typography>
              <Chip
                label="A person with a disability"
                className={classes.previewTag}
              />
            </Box>

            <Box mt={4}>
              <Typography variant="h6">
                If you'd like to be more specific select all that apply. If you do, this info will be visible on your profile.
              </Typography>

              <FormGroup className={classes.checkboxContainer}>
                <Box className={classes.checkboxWrapper}>
                  <VisibleCheckbox
                    className={classes.checkbox}
                    checked={selectedDisabilities.includes('Neurodivergent')}
                    onChange={handleDisabilityChange}
                    value="Neurodivergent"
                  />
                  <Box className={classes.labelContainer}>
                    <Typography>Neurodivergent</Typography>
                    <Typography variant="caption" color="textSecondary">
                      (ie. ADHD, autism, bipolar disorder, cognitive disabilities and/or developmental challenges, learning disabilities, other mental health disabilities)
                    </Typography>
                  </Box>
                </Box>

                <Box className={classes.checkboxWrapper}>
                  <VisibleCheckbox
                    className={classes.checkbox}
                    checked={selectedDisabilities.includes('Blind or low vision')}
                    onChange={handleDisabilityChange}
                    value="Blind or low vision"
                  />
                  <Box className={classes.labelContainer}>
                    <Typography>Blind or low vision</Typography>
                    <Typography variant="caption" color="textSecondary">
                      (Not including vision correctable with glasses)
                    </Typography>
                  </Box>
                </Box>

                <Box className={classes.checkboxWrapper}>
                  <VisibleCheckbox
                    className={classes.checkbox}
                    checked={selectedDisabilities.includes('Deaf or hard-of-hearing')}
                    onChange={handleDisabilityChange}
                    value="Deaf or hard-of-hearing"
                  />
                  <Box className={classes.labelContainer}>
                    <Typography>Deaf or hard-of-hearing</Typography>
                  </Box>
                </Box>

                <Box className={classes.checkboxWrapper}>
                  <VisibleCheckbox
                    className={classes.checkbox}
                    checked={selectedDisabilities.includes('Living with physical disabilities requiring accommodations and/or mobility aids')}
                    onChange={handleDisabilityChange}
                    value="Living with physical disabilities requiring accommodations and/or mobility aids"
                  />
                  <Box className={classes.labelContainer}>
                    <Typography>Living with physical disabilities requiring accommodations and/or mobility aids</Typography>
                  </Box>
                </Box>

                <Box className={classes.checkboxWrapper}>
                  <VisibleCheckbox
                    className={classes.checkbox}
                    checked={selectedDisabilities.includes('Living with physical illness and/or chronic pain')}
                    onChange={handleDisabilityChange}
                    value="Living with physical illness and/or chronic pain"
                  />
                  <Box className={classes.labelContainer}>
                    <Typography>Living with physical illness and/or chronic pain</Typography>
                    <Typography variant="caption" color="textSecondary">
                      (diabetes, epilepsy, arthritis, etc.)
                    </Typography>
                  </Box>
                </Box>

                <Box className={classes.checkboxWrapper}>
                  <VisibleCheckbox
                    className={classes.checkbox}
                    checked={selectedDisabilities.includes('Living with intellectual disabilities')}
                    onChange={handleDisabilityChange}
                    value="Living with intellectual disabilities"
                  />
                  <Box className={classes.labelContainer}>
                    <Typography>Living with intellectual disabilities</Typography>
                  </Box>
                </Box>

                <Box className={classes.checkboxWrapper}>
                  <VisibleCheckbox
                    className={classes.checkbox}
                    checked={selectedDisabilities.includes('Living with speech or language disabilities')}
                    onChange={handleDisabilityChange}
                    value="Living with speech or language disabilities"
                  />
                  <Box className={classes.labelContainer}>
                    <Typography>Living with speech or language disabilities</Typography>
                  </Box>
                </Box>

                <Box className={classes.checkboxWrapper}>
                  <VisibleCheckbox
                    className={classes.checkbox}
                    checked={selectedDisabilities.includes('None of the above describes how I identify')}
                    onChange={handleDisabilityChange}
                    value="None of the above describes how I identify"
                  />
                  <Box className={classes.labelContainer}>
                    <Typography>None of the above describes how I identify</Typography>
                  </Box>
                </Box>

                <Box className={classes.checkboxWrapper}>
                  <VisibleCheckbox
                    className={classes.checkbox}
                    checked={selectedDisabilities.includes('I prefer not to answer')}
                    onChange={handleDisabilityChange}
                    value="I prefer not to answer"
                  />
                  <Box className={classes.labelContainer}>
                    <Typography>I prefer not to answer</Typography>
                  </Box>
                </Box>
              </FormGroup>
            </Box>
          </>
        )}
      </Box>

      {onboarding && (
        <ProgressBarFooter
          valid={!!disabilityStatus}
          stepIndex={stepIndex}
          firstStep={stepIndex}
          numSteps={totalSteps}
          onNext={handleSubmit}
          onPrev={() => { }}
        />
      )}

      {!onboarding && (
        <Box mx="auto" my={3} maxWidth={380} display="flex" justifyContent="center" flexWrap="wrap" style={{ gap: '1rem' }}>
          <Button
            variant="contained"
            style={{ backgroundColor: buttonColor, color: 'white', minWidth: 180 }}
            onClick={handleSubmit}
            disabled={!disabilityStatus}
          >
            Save Information
          </Button>

          <Button
            variant="contained"
            style={{ backgroundColor: buttonColor, color: 'white', minWidth: 180 }}
            onClick={() => history.push('/profile')}
          >
            Back to Profile
          </Button>
        </Box>
      )}
    </Frame>
  )
}

export default FreelancerDisabilitySelect
